import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { useAppSelector, useAppDispatch } from '@common-reducers/hooks/store.hook';
import { setCurrentDate } from '@common/reducers/CalendarReducer';
import { getAgendaFlattenedData } from '@common/reducers/CalendarSelectors';
import { editCalendarEventThunk } from '@common/reducers/CalendarThunks';
import { MediaPreviewIcon } from '@common/views/user-overview/tabs/messages/components/MediaPreview';
import { Check, CheckCircle, Error, EventNote, Favorite, Schedule } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { isRtlLanguage } from '@common-services/rtl';
import { applyMomentLocale, getMomentLocale } from '@common-config/moment-locales';
import CalendarSidebarCampaignRow from '../../components/scheduled-contact-list/calendar-sidebar-campaign-row';
import CalendarSidebarMessageRow from '../../components/scheduled-contact-list/calendar-sidebar-message-row';
import CustomToolbar from '../calendar/custom-toolbar';
import classes from './agenda-view.module.scss';


interface AgendaViewProps {
  // Props can be added here if needed in the future
}

interface PollOptionsRowProps {
  // Matches `Poll.options` from bt-common — `{id, text, order}` per element.
  options: NonNullable<UserMessage['poll']>['options'];
}

function PollOptionsRow({ options }: PollOptionsRowProps) {
  const safeOptions = options ?? [];
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(safeOptions.length);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    let totalWidth = 0;
    let count = 0;

    // Create temporary elements to measure width
    const tempContainer = document.createElement('div');
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.position = 'absolute';
    tempContainer.style.display = 'flex';
    tempContainer.style.gap = '4px';
    document.body.appendChild(tempContainer);

    try {
      for (let i = 0; i < safeOptions.length; i++) {
        const option = safeOptions[i];
        if (!option) continue;
        const text = typeof option === 'string' ? option : option.text;

        // Create option element
        const optionEl = document.createElement('span');
        optionEl.textContent = text;
        optionEl.style.cssText = `
          background-color: var(--color-surface-muted);
          color: var(--color-text);
          padding: 2px 4px;
          border-radius: 4px;
          font-size: 0.875rem;
          white-space: nowrap;
          max-width: 80px;
          overflow: hidden;
          text-overflow: ellipsis;
        `;
        tempContainer.appendChild(optionEl);

        const optionWidth = optionEl.offsetWidth;

        // Check if we need "+x more" space
        const remainingOptions = safeOptions.length - i - 1;
        let moreTextWidth = 0;
        if (remainingOptions > 0) {
          const moreEl = document.createElement('span');
          moreEl.textContent = `+${remainingOptions} more`;
          moreEl.style.cssText = 'font-size: 0.875rem; color: var(--color-text-muted); opacity: 0.7;';
          tempContainer.appendChild(moreEl);
          moreTextWidth = moreEl.offsetWidth;
          tempContainer.removeChild(moreEl);
        }

        const newTotalWidth = totalWidth + optionWidth + (i > 0 ? 4 : 0);

        if (newTotalWidth + moreTextWidth > containerWidth && count > 0) {
          break;
        }

        totalWidth = newTotalWidth;
        count++;
      }
    } finally {
      document.body.removeChild(tempContainer);
    }

    setVisibleCount(Math.max(1, count)); // Show at least 1 option
  }, [safeOptions]);

  return (
    <Stack
      ref={containerRef}
      direction="row"
      spacing={0.5}
      sx={{
        mt: 0.5,
        overflow: 'hidden',
        width: '100%'
      }}
    >
      {safeOptions.slice(0, visibleCount).map((option, index) => {
        if (!option) return null;
        const text = typeof option === 'string' ? option : option.text;
        const key = typeof option === 'string' ? `${index}-${option}` : (option.id ?? `${index}-${option.text}`);
        return (
          <Typography
            key={key}
            variant="body2"
            sx={{
              bgcolor: 'var(--color-surface-muted)',
              color: 'var(--color-text)',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '80px'
            }}
          >
            {text}
          </Typography>
        );
      })}
      {visibleCount < safeOptions.length && (
        <Typography
          variant="body2"
          sx={{
            color: 'var(--color-text-muted)',
            opacity: 0.7
          }}
        >
          +{safeOptions.length - visibleCount} more
        </Typography>
      )}
    </Stack>
  );
}

const getStatusIcon = (message: UserMessage) => {
  if (!message.status) return <Schedule fontSize="inherit" />;

  switch (message.status.toLowerCase()) {
    case 'sent':
      return <Check fontSize="inherit" />;
    case 'delivered':
      return <CheckCircle fontSize="inherit" />;
    case 'failed':
      return <Error fontSize="inherit" />;
    default:
      return <Schedule fontSize="inherit" />;
  }
};

const EmptyStateComponent = ({ currentDate, locale }: { currentDate: Date; locale: string }) => {
  const monthYear = moment(currentDate).locale(getMomentLocale(locale)).format('MMMM YYYY');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100% - 98px)',
        padding: 4,
        textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(var(--agenda-accent-rgb), 0.03) 0%, transparent 70%)',
          animation: 'gentle-float 8s ease-in-out infinite',
          zIndex: 0,
        },
        '@keyframes gentle-float': {
          '0%, 100%': {
            transform: 'translate(-50%, -50%) scale(1)',
          },
          '50%': {
            transform: 'translate(-50%, -50%) scale(1.1)',
          },
        },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: 'var(--color-text)',
          fontWeight: 500,
          mb: 1,
          opacity: 0.9,
        }}
      >
        <strong>{monthYear}</strong>
      </Typography>


      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: '400px',
        }}
      >
        {/* Icon Container */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(var(--agenda-accent-rgb), 0.1) 0%, rgba(var(--agenda-accent-rgb), 0.05) 100%)',
            border: '2px solid rgba(var(--agenda-accent-rgb), 0.1)',
            mb: 1,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: -2,
              borderRadius: '50%',
              padding: '2px',
              background: 'linear-gradient(135deg, rgba(var(--agenda-accent-rgb), 0.2), rgba(var(--agenda-accent-rgb), 0.1))',
              mask: 'linear-gradient(var(--color-surface) 0 0) content-box, linear-gradient(var(--color-surface) 0 0)',
              maskComposite: 'xor',
              animation: 'gentle-pulse 3s ease-in-out infinite',
            },
            '@keyframes gentle-pulse': {
              '0%, 100%': {
                opacity: 0.3,
              },
              '50%': {
                opacity: 0.6,
              },
            },
          }}
        >
          <EventNote
            sx={{
              fontSize: 40,
              color: 'rgba(var(--agenda-accent-rgb), 0.7)',
            }}
          />
        </Box>



        {/* Sub Message */}
        <Typography
          variant="body2"
          sx={{
            color: 'var(--color-text-muted)',
            opacity: 0.7,
            lineHeight: 1.6,
          }}
        >
          <strong>Your agenda is clear for this month.</strong>
          <br />
          Use the navigation to explore other months or schedule new message (with the plus button).
        </Typography>

      </Box>
    </Box>
  );
};

const AgendaView = ({ }: AgendaViewProps) => {
  const { t, i18n } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const flattenedData = useAppSelector(state => getAgendaFlattenedData(state));
  const currentDate = useAppSelector((state) => state.CalendarReducer.currentDate);
  const activeLanguage = i18n.resolvedLanguage || i18n.language || 'en';
  const momentLocale = getMomentLocale(activeLanguage);
  const isRtl = isRtlLanguage(activeLanguage);

  useEffect(() => {
    applyMomentLocale(activeLanguage);
  }, [activeLanguage]);

  const handleNavigate = (action: 'PREV' | 'NEXT' | 'TODAY' | 'DATE') => {
    let newDate: Date;
    switch (action) {
      case 'PREV':
        newDate = moment(currentDate).subtract(1, 'month').toDate();
        break;
      case 'NEXT':
        newDate = moment(currentDate).add(1, 'month').toDate();
        break;
      case 'TODAY':
        newDate = new Date();
        break;
      case 'DATE':
        return;
      default:
        return;
    }
    dispatch(setCurrentDate(newDate));
  };

  const handleMessageClick = (message: UserMessage, event: React.MouseEvent) => {
    event.stopPropagation();


    // Detect if this is a campaign
    const isCampaign = !!(message as any).campaignStatus || !!(message as any).template;

    // Use editCalendarEventThunk instead of onEventSelect to properly set selectedEventDate
    const leanEvent = {
      _id: message._id,
      isCampaign,
      dueDate: message.dueDate?.toString() ?? '',
      isRecurring: message.isRecurring || false
    };

    dispatch(editCalendarEventThunk(leanEvent));
  };

  const getMessageIcon = (message: UserMessage) => {
    const hasHeartEmoji = message.message?.match(/^[❤️🧡💛💚💙💜🤎🖤🤍]/);

    if (hasHeartEmoji || message.isPoll) {
      return (
        <Favorite
          fontSize="small"
          color="error"
          sx={{ opacity: 0.7 }}
          titleAccess="Marketing Message"
        />
      );
    }
    if (message.asset?.url) {
      return <MediaPreviewIcon assetData={message.asset} />;
    }
    return null;
  };

  const renderMessageText = (message: UserMessage) => {
    if (message.isPoll && message.poll?.question) {
      return (
        <>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--color-text)' }}>
            Poll: {message.poll.question}
          </Typography>
          {message.poll.options && message.poll.options.length > 0 && (
            <PollOptionsRow options={message.poll.options} />
          )}
        </>
      );
    }

    if (message.asset?.url) {
      return (
        <Box>
            <Typography
            variant="body2"
            sx={{
                fontStyle: 'italic',
                color: 'var(--color-text)',
              cursor: 'pointer'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleMessageClick(message, e);
            }}
          >
            {message.asset.name || 'Media attachment'}
          </Typography>
            {message.message && (
            <Typography variant="body2" sx={{ color: 'var(--color-text)', mt: 0.5 }}>
              {message.message}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ color: 'var(--color-text)' }}>
        {message.message || ''}
      </Typography>
    );
  };

  const renderCampaignText = (message: UserMessage) => {
    const campaignMessage = (message as any).template?.message || '';

    return (
      <Typography variant="body2" sx={{ color: 'var(--color-text)', fontWeight: 500 }}>
        {campaignMessage || 'Campaign'}
      </Typography>
    );
  };

  const ItemContent = (index: number) => {
    const item = flattenedData[index];

    if (item.type === 'header') {
      const dateMoment = moment(item.date).locale(momentLocale);
      return (
        <div className={classes.headerContainer} style={{ paddingTop: index === 0 ? 0 : undefined }}>
          <Typography variant="body2" className={classes.headerText}>
            {dateMoment.format('ll')}
          </Typography>
          <Typography variant="caption" className={classes.headerDay}>
            {dateMoment.format('dddd')}
          </Typography>
        </div>
      );
    }

    const message = item.message;

    // Detect if this is a campaign
    const isCampaign = !!(message as any).campaignStatus || !!(message as any).template;

    return (
      <Box
        data-message-id={message.id}
        style={{
          padding: '6px 16px',
        }}
      >
        {isCampaign ? (
          <CalendarSidebarCampaignRow
            campaignId={message._id}
          />
        ) : (
          <CalendarSidebarMessageRow
            messageId={message._id}
            occurrenceDueDate={typeof message.dueDate === 'string' ? message.dueDate : message.dueDate?.toISOString()}
          />
        )}
      </Box>
    );
  };

  return (
    <div className={classes.agendaContainer} dir={isRtl ? 'rtl' : 'ltr'}>

      {/* <AgendaToolbar
        currentDate={currentDate}
        onNavigate={handleNavigate}
      /> */}

      <CustomToolbar
        toolbarProps={{
          date: currentDate,
          onNavigate: handleNavigate,
          label: moment(currentDate).locale(momentLocale).format('MMMM YYYY'),
          view: 'month',
          onView: () => { },
          views: ['month', 'week', 'day'],
          localizer: {
            messages: {
              month: t('calendar.month'),
              week: t('calendar.week'),
              day: t('calendar.day'),
              today: t('calendar.today'),
              previous: t('calendar.previous'),
              next: t('calendar.next'),
            }
          },
        }}
      />

      {flattenedData.length === 0 ? (
        <EmptyStateComponent currentDate={currentDate} locale={activeLanguage} />
      ) : (
        <Virtuoso
          style={{ height: 'calc(100% - 70px)' }} // Account for toolbar height
          totalCount={flattenedData.length}
          itemContent={ItemContent}
          overscan={200}
        />
      )}
    </div>
  );
};

export default AgendaView; 
